import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import PanelWrapper from "app/components/PanelWrapper";
import PublicWrapper from "app/components/PublicWrapper";
import { Spinner } from "app/components/Loading";
import settings from "config/environment";

interface PurchaseCancelParams {
  id: string;
}

const PurchaseCancel: React.FC = () => {
  const { id } = useParams<PurchaseCancelParams>();
  const history = useHistory();
  const { t } = useTranslation();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const isMobile = ["true"].includes((searchParams.get("mobile") || "").toLowerCase());

  const [attemptedDeepLink, setAttemptedDeepLink] = useState(false);
  const [deepLinkUrl, setDeepLinkUrl] = useState<string | null>(null);
  const [hasCancelled, setHasCancelled] = useState(false);

  useEffect(() => {
    const handleCancelRedirect = async () => {
      try {
        // Call cancel API to get redirect data
        const response = await schedulingFetchPost(`/stripe_purchases/${id}/cancel`, {});
        const { programIds, coachProfileId, purchaseItems } = response;
        const itemType = purchaseItems?.[0]?.type;

        // Determine redirect path and mobile type based on purchase item type
        let pathWeb: string;
        let mobileType: string;

        if (itemType === "program_registration") {
          const programId = programIds?.[0] || purchaseItems?.[0]?.details?.programId;
          pathWeb = programId ? `/programs/${programId}/view` : `/programs`;
          mobileType = "program";
        } else if (itemType === "time_slot_reservation" || itemType === "lesson_schedule") {
          pathWeb = `/sessions/new`;
          mobileType = "session";
        } else if (itemType === "stripe_product") {
          if (coachProfileId) {
            pathWeb = `/home/${coachProfileId}`;
          } else {
            pathWeb = `/sessions`;
          }
          mobileType = "offering";
        } else {
          pathWeb = `/`;
          mobileType = "purchase";
        }

        console.log("Cancel redirect - itemType:", itemType, "pathWeb:", pathWeb);

        if (isMobile) {
          const qs = new URLSearchParams({
            purchase_id: String(id ?? ""),
            type: mobileType,
          });
          const deepLink = `${settings.MOBILE_URL}/purchase/cancel?${qs.toString()}`;

          setDeepLinkUrl(deepLink);

          if (!attemptedDeepLink) {
            setAttemptedDeepLink(true);
            window.location.href = deepLink;
          }
          return;
        }

        history.replace(pathWeb);
      } catch (err) {
        console.error("Error cancelling purchase:", err);
        // On error, redirect to home
        history.replace("/");
      }
    };

    if (id && !hasCancelled) {
      setHasCancelled(true);
      handleCancelRedirect();
    }
  }, [id]);

  return (
    <PublicWrapper>
      <PanelWrapper>
        {!attemptedDeepLink && (
          <div className="text-center mt-10 flex flex-col items-center gap-4">
            <div>{t("Processing, please wait...")}</div>
            <br />
            <Spinner />
          </div>
        )}

        {attemptedDeepLink && (
          <div className="text-center mt-10 flex flex-col items-center gap-4 px-4">
            <div className="text-xl font-semibold">{t("Launching App...")}</div>
            <Spinner />
            {deepLinkUrl && (
              <div>
                <Trans i18nKey="paymentsRedirect">
                  If nothing happens, tap <a href={deepLinkUrl}>here</a> to continue
                </Trans>
              </div>
            )}
          </div>
        )}
      </PanelWrapper>
    </PublicWrapper>
  );
};

export default PurchaseCancel;
